import { useCallback, useEffect, useRef, useState } from "react";
import classNames from "classnames";

import { Image } from "../image/image";
import styles from "./zoomableImage.module.css";

export const ZoomableImage = ({
    src,
    alt = "",
    className,
    objectFit = "contain",
    zoomMin = 0.5,
    zoomMax = 5,
    zoomStep = 0.25,
    ...props
}) => {
    const [zoom, setZoom] = useState(1);
    const [pan, setPan] = useState({ x: 0, y: 0 });

    const containerRef = useRef(null);
    const [containerSize, setContainerSize] = useState({ w: 0, h: 0 });
    const [naturalSize, setNaturalSize] = useState({ w: 0, h: 0 });

    const panStartRef = useRef(null);

    const clamp = useCallback((value, min, max) => Math.min(max, Math.max(min, value)), []);

    const getDisplayedSize = useCallback(() => {
        const cw = containerSize.w;
        const ch = containerSize.h;
        const nw = naturalSize.w;
        const nh = naturalSize.h;

        if (!cw || !ch) return { w: 0, h: 0 };
        if (!nw || !nh) return { w: cw, h: ch };

        const scaleX = cw / nw;
        const scaleY = ch / nh;

        const baseScale = objectFit === "cover" ? Math.max(scaleX, scaleY) : Math.min(scaleX, scaleY);

        return {
            w: nw * baseScale,
            h: nh * baseScale,
        };
    }, [containerSize.h, containerSize.w, naturalSize.h, naturalSize.w, objectFit]);

    const clampPan = useCallback((nextPan, nextZoom) => {
        const cw = containerSize.w;
        const ch = containerSize.h;
        if (!cw || !ch) return nextPan;

        const base = getDisplayedSize();
        if (!base.w || !base.h) return { x: 0, y: 0 };

        const displayedW = base.w * nextZoom;
        const displayedH = base.h * nextZoom;

        const maxX = Math.max(0, (displayedW - cw) / 2);
        const maxY = Math.max(0, (displayedH - ch) / 2);

        return {
            x: clamp(nextPan.x, -maxX, maxX),
            y: clamp(nextPan.y, -maxY, maxY),
        };
    }, [clamp, containerSize.h, containerSize.w, getDisplayedSize]);

    const resetView = useCallback(() => {
        setZoom(1);
        setPan({ x: 0, y: 0 });
        panStartRef.current = null;
    }, []);

    useEffect(() => {
        const el = containerRef.current;
        if (!el) return;

        const updateSize = () => {
            const rect = el.getBoundingClientRect();
            setContainerSize({ w: rect.width || 0, h: rect.height || 0 });
        };

        updateSize();

        if (typeof ResizeObserver === "undefined") {
            window.addEventListener("resize", updateSize);
            return () => window.removeEventListener("resize", updateSize);
        }

        const ro = new ResizeObserver(() => updateSize());
        ro.observe(el);
        return () => ro.disconnect();
    }, []);

    useEffect(() => {
        if (!src) {
            setNaturalSize({ w: 0, h: 0 });
            return;
        }

        const img = new window.Image();
        img.onload = () => {
            setNaturalSize({ w: img.naturalWidth || 0, h: img.naturalHeight || 0 });
        };
        img.onerror = () => {
            setNaturalSize({ w: 0, h: 0 });
        };
        img.src = src;

        return () => {
            img.onload = null;
            img.onerror = null;
        };
    }, [src]);

    useEffect(() => {
        resetView();
    }, [src, resetView]);

    useEffect(() => {
        setPan((prev) => {
            const clamped = clampPan(prev, zoom);
            if (clamped.x === prev.x && clamped.y === prev.y) return prev;
            return clamped;
        });
    }, [clampPan, zoom]);

    const onWheel = useCallback((e) => {
        e.preventDefault();

        const direction = e.deltaY > 0 ? 1 : -1;

        setZoom((prev) => clamp(prev + zoomStep * direction, zoomMin, zoomMax));
    }, [clamp, zoomMax, zoomMin, zoomStep]);

    const onPointerDown = useCallback((e) => {
        if (zoom <= 1) return;
        if (e.button !== undefined && e.button !== 0) return;

        e.preventDefault();

        const clampedPan = clampPan(pan, zoom);
        if (clampedPan.x !== pan.x || clampedPan.y !== pan.y) {
            setPan(clampedPan);
        }

        panStartRef.current = {
            pointerId: e.pointerId,
            startX: e.clientX,
            startY: e.clientY,
            panX: clampedPan.x,
            panY: clampedPan.y,
        };

        try {
            e.currentTarget.setPointerCapture(e.pointerId);
        } catch {
            // ignore
        }
    }, [clampPan, pan, zoom]);

    const onPointerMove = useCallback((e) => {
        const start = panStartRef.current;
        if (!start) return;
        if (start.pointerId !== e.pointerId) return;

        e.preventDefault();

        const dx = e.clientX - start.startX;
        const dy = e.clientY - start.startY;

        const nextPan = {
            x: start.panX + dx,
            y: start.panY + dy,
        };
        setPan(clampPan(nextPan, zoom));
    }, [clampPan, zoom]);

    const onPointerUp = useCallback((e) => {
        const start = panStartRef.current;
        if (!start) return;
        if (start.pointerId !== e.pointerId) return;

        e.preventDefault();
        panStartRef.current = null;

        try {
            e.currentTarget.releasePointerCapture(e.pointerId);
        } catch {
            // ignore
        }
    }, []);

    return (
        <div
            className={classNames(styles.container, className, { [styles.pannable]: zoom > 1 })}
            ref={containerRef}
            onWheel={onWheel}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerUp}
            {...props}
        >
            <div
                className={styles.transform}
                style={{
                    transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
                }}
            >
                <Image
                    src={src}
                    alt={alt}
                    width="100%"
                    height="100%"
                    objectFit={objectFit}
                    className={styles.image}
                    draggable={false}
                    onDragStart={(e) => e.preventDefault()}
                />
            </div>
        </div>
    );
};
